import React, { useState, useRef } from "react";
import { Card, Row, Col, Typography } from "antd";
import { SafetyOutlined, LineChartOutlined } from "@ant-design/icons";
import "./HomePage.css";
import ceramicVase from "../images/清乾隆.jpg";
import ancientPainting from "../images/清明上河圖.jpg";
import jadeArtifact from "../images/古玉.jpg";
import modernArt from "../images/6.當代藝術 (3).jpg";
import ancientBronze from "../images/3.古青銅器.jpg";
import riskPrevention from "../images/4.法庭槌 (4).jpg";
import marketAnalysis from "../images/5.拍賣行 (2).jpg";
import { getChainlitUrl } from "../services/chainlitUrls";

const { Title, Paragraph, Text } = Typography;

// External chat URL (env override supported)
const CHAT_URL = getChainlitUrl();

interface HomePageProps {
  onNavigate: (key: string) => void;
  onStartChat?: (query: string) => void;
  onNavigateToDemo?: () => void;
}

interface FeatureItem {
  key: string;
  icon: string;
  title: string;
  description: string;
  details: string[];
}

const HomePage: React.FC<HomePageProps> = ({ onNavigate, onStartChat }) => {
  const [inputValue, setInputValue] = useState<string>("");
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [startX, setStartX] = useState<number>(0);
  const [scrollLeft, setScrollLeft] = useState<number>(0);
  const carouselRef = useRef<HTMLDivElement>(null);

  const handleSend = (): void => {
    if (inputValue.trim()) {
      if (onStartChat) {
        onStartChat(inputValue);
      } else {
        onNavigate("smart-query");
      }
      setInputValue("");
    }
  };

  const features: FeatureItem[] = [
    {
      key: "modern-art",
      icon: modernArt,
      title: "當代藝術",
      description: "",
      details: ["鑑證", "備案"],
    },
    {
      key: "ancient-ceramic",
      icon: ceramicVase,
      title: "古陶瓷",
      description: "",
      details: ["年代鑑定", "工藝分析"],
    },
    {
      key: "paintings",
      icon: ancientPainting,
      title: "古書畫",
      description: "",
      details: ["點評", "建議"],
    },
    {
      key: "jade",
      icon: jadeArtifact,
      title: "古玉器",
      description: "",
      details: ["點評", "建議"],
    },
    {
      key: "ancient-bronze",
      icon: ancientBronze,
      title: "古青銅",
      description: "",
      details: ["點評", "建議"],
    },
    {
      key: "risk-prevention",
      icon: riskPrevention,
      title: "風險規避防騙",
      description: "",
      details: ["造假辨識", "防騙認知"],
    },
    {
      key: "market-analysis",
      icon: marketAnalysis,
      title: "市場行情朔源",
      description: "",
      details: ["行情溯源", "推薦服務"],
    },
  ];

  const renderIcon = (feature: FeatureItem) => {
    console.log(
      `Rendering icon for: ${feature.title} with src: ${feature.icon}`
    );
    return React.createElement("img", {
      src: feature.icon,
      alt: feature.title,
      style: {
        width: "96px",
        height: "96px",
        borderRadius: "12px",
        objectFit: "cover",
      },
      onError: (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
        console.error(`Image failed to load: ${feature.title} ${feature.icon}`);
      },
      onLoad: () => {
        console.log(`Image loaded successfully: ${feature.title}`);
      },
    });
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!carouselRef.current) return;
    setIsDragging(true);
    setStartX(e.touches[0].clientX);
    setScrollLeft(carouselRef.current.scrollLeft);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || !carouselRef.current) return;
    e.preventDefault();
    const x = e.touches[0].clientX;
    const walk = (x - startX) * 2;
    carouselRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    if (!carouselRef.current) return;

    const cardWidth = 177;
    const newIndex = Math.round(carouselRef.current.scrollLeft / cardWidth);
    setCurrentIndex(Math.max(0, Math.min(newIndex, features.length - 1)));

    carouselRef.current.scrollTo({
      left: newIndex * cardWidth,
      behavior: "smooth",
    });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!carouselRef.current) return;
    setIsDragging(true);
    setStartX(e.clientX);
    setScrollLeft(carouselRef.current.scrollLeft);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !carouselRef.current) return;
    e.preventDefault();
    const x = e.clientX;
    const walk = (x - startX) * 2;
    carouselRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (!carouselRef.current) return;

    const cardWidth = 177;
    const newIndex = Math.round(carouselRef.current.scrollLeft / cardWidth);
    setCurrentIndex(Math.max(0, Math.min(newIndex, features.length - 1)));

    carouselRef.current.scrollTo({
      left: newIndex * cardWidth,
      behavior: "smooth",
    });
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      handleMouseUp();
    }
  };

  const handleIndicatorClick = (index: number) => {
    if (carouselRef.current) {
      const cardWidth = 177;
      carouselRef.current.scrollTo({
        left: index * cardWidth,
        behavior: "smooth",
      });
      setCurrentIndex(index);
    }
  };

  return React.createElement(
    "div",
    { className: "home-page fullscreen-layout" },
    React.createElement(
      "div",
      { className: "main-content" },
      React.createElement(
        Title,
        {
          level: 1,
          style: { margin: "0 0 24px 0", color: "#001529" },
        },
        "文藝品AI智能諮詢服務平台"
      ),

      React.createElement(
        "div",
        {
          className: "cards-container",
          style: {
            overflowX: "auto",
            padding: "20px 0",
            cursor: isDragging ? "grabbing" : "grab",
            userSelect: "none",
          },
          ref: carouselRef,
          onTouchStart: handleTouchStart,
          onTouchMove: handleTouchMove,
          onTouchEnd: handleTouchEnd,
          onMouseDown: handleMouseDown,
          onMouseMove: handleMouseMove,
          onMouseUp: handleMouseUp,
          onMouseLeave: handleMouseLeave,
        },
        React.createElement(
          Row,
          {
            style: {
              flexWrap: "nowrap",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              display: "flex",
              gap: "20px",
            },
          },
          features.map((feature) =>
            React.createElement(
              Col,
              {
                key: feature.key,
                style: {
                  flex: "none",
                  width: "164px",
                },
              },
              React.createElement(
                Card,
                {
                  hoverable: true,
                  className: "feature-card",
                  onClick: (e: React.MouseEvent) => {
                    e.preventDefault();
                    e.stopPropagation();
                    window.location.href = CHAT_URL;
                  },
                  style: {
                    height: "336px",
                    textAlign: "center",
                    background: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(15px)",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
                    borderRadius: "16px",
                    pointerEvents: "auto",
                  },
                },
                React.createElement(
                  "div",
                  {
                    style: {
                      marginBottom: "24px",
                      paddingTop: "20px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    },
                  },
                  React.createElement(
                    "div",
                    {
                      style: {
                        width: "100%",
                        height: "106px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      },
                    },
                    renderIcon(feature)
                  )
                ),
                React.createElement(
                  Title,
                  {
                    level: 4,
                    style: {
                      marginBottom: "16px",
                      fontSize: "18px",
                      color: "#001529",
                    },
                  },
                  feature.title
                ),
                React.createElement(
                  "div",
                  {
                    className: "feature-details",
                    style: { marginTop: "16px", padding: "0 16px" },
                  },
                  feature.details.map((detail, index) =>
                    React.createElement(
                      Text,
                      {
                        key: index,
                        style: {
                          display: "block",
                          fontSize: "14px",
                          color: "#666",
                          marginBottom: "8px",
                        },
                      },
                      "• " + detail
                    )
                  )
                )
              )
            )
          )
        )
      ),

      React.createElement(
        "div",
        {
          style: {
            display: "flex",
            justifyContent: "center",
            marginTop: "20px",
            gap: "8px",
          },
        },
        features.map((_, index) =>
          React.createElement("div", {
            key: index,
            style: {
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              backgroundColor:
                index === currentIndex ? "#1890ff" : "rgba(255, 255, 255, 0.5)",
              transition: "background-color 0.3s ease",
              cursor: "pointer",
            },
            onClick: () => handleIndicatorClick(index),
          })
        )
      ),

      React.createElement(
        "div",
        { style: { marginTop: "48px", textAlign: "center" } },
        React.createElement(
          Title,
          {
            level: 2,
            style: {
              color: "white",
              fontSize: "28px",
              fontWeight: 600,
              textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
              margin: 0,
            },
          },
          "智元AI研究室2025"
        )
      )
    )
  );
};

export default HomePage;
